import { Flex, Spacing, Text } from '@billy/design-system'
import { type ReactElement, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'

import { DEFAULT_INVOICE_LANGUAGE } from '@features/invoices/constants/defaultInvoiceLanguage'
import { invoiceLocaleToLanguage } from '@features/invoices/constants/invoiceLocaleToLanguage'
import { LIVE_PREVIEW_DEBOUNCE_TIME } from '@features/invoices/constants/livePreviewDebounceTime'
import { InvoiceDocumentType } from '@features/invoices/enums/invoiceDocumentType'

import { useDebouncedValue } from '../../../../../../hooks/useDebouncedValue'
import { useForm } from '../../../../../../hooks/useForm'
import { useRegisterInvoicingSettingsForm } from '../../../../hooks/useRegisterInvoicingSettingsForm'
import { useRegisterInvoicingSettingsPreviewPatch } from '../../../../hooks/useRegisterInvoicingSettingsPreviewPatch'
import type {
  InvoicingSettingsFormSaveOptions,
  InvoicingSettingsFormSaveResult,
} from '../../../../types/invoicingSettingsFormSave'
import { usePredefinedPresetsSettingsFormValues } from '../../hooks/usePredefinedPresetsSettingsFormValues'
import { getPresetsPreviewPatch } from '../../utils/getPresetsPreviewPatch'
import { getPresetsSettingsTradeNames } from '../../utils/getPresetsSettingsTradeNames'
import { PresetsSettingsFormFields } from './elements/PresetsSettingsFormFields'
import type { PresetsSettingsFormSchema } from './types/presetsSettingsFormSchema'
import { getPresetsSettingsDefaultValues } from './utils/getPresetsSettingsDefaultValues'
import { getPresetsSettingsFormSchema } from './utils/getPresetsSettingsFormSchema'

export const PresetsSettingsForm = (): ReactElement => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { hasFailedToLoad, isLoading, predefinedPresetsSettingsFormValues, refetch } =
    usePredefinedPresetsSettingsFormValues()
  const { Form, formState, getValues, handleSubmit, reset, watch } = useForm<PresetsSettingsFormSchema>({
    defaultValues: getPresetsSettingsDefaultValues(),
    validationSchema: getPresetsSettingsFormSchema(t),
  })

  const { isDirty } = formState
  const hasLoadedSettings = !isLoading && !hasFailedToLoad
  const { contactEmail, contactFirstName, contactLastName, contactPhone, currency, language } = watch()
  // Memoized on the strings: a new object on every render would restart the debounce forever.
  const contactValues = useMemo(
    () => ({ contactEmail, contactFirstName, contactLastName, contactPhone }),
    [contactEmail, contactFirstName, contactLastName, contactPhone],
  )
  // Undefined until the loaded contact settles, so the empty pre-load defaults never reach the preview.
  const debouncedContactValues = useDebouncedValue(
    hasLoadedSettings ? contactValues : undefined,
    LIVE_PREVIEW_DEBOUNCE_TIME,
  )

  const handleFormReset = useCallback(() => {
    reset(getValues())
  }, [getValues, reset])

  const handleSave = useCallback(
    ({ updateSettings }: InvoicingSettingsFormSaveOptions): Promise<InvoicingSettingsFormSaveResult> => {
      if (!hasLoadedSettings) {
        return Promise.reject(new Error('Presets settings are not loaded'))
      }

      // handleSubmit passes Zod's parsed values and resolves 'invalid' without sending when the email fails.
      return new Promise((resolve, reject) => {
        handleSubmit(
          async (values) => {
            const { currency, language, productType } = values
            const tradeNames = getPresetsSettingsTradeNames(values, queryClient)
            const sharedFieldPatch = {
              language: { default: invoiceLocaleToLanguage[language] || DEFAULT_INVOICE_LANGUAGE },
              currency: { default: currency },
            }

            await updateSettings({
              modelPatches: {
                // The Quote Editor reads item type from the invoice model (SILC-2914); a fresh
                // org's estimate model has no productType field at all, so patching it there
                // would throw and block every other field in this save.
                [InvoiceDocumentType.Invoice]: { ...sharedFieldPatch, productType: { default: productType } },
                [InvoiceDocumentType.Quote]: sharedFieldPatch,
              },
              values: { company: { tradeNames } },
            })

            resolve('saved')
          },
          () => {
            resolve('invalid')
          },
        )().catch(reject)
      })
    },
    [handleSubmit, hasLoadedSettings, queryClient],
  )

  useRegisterInvoicingSettingsForm({ isDirty, resetForm: handleFormReset, save: handleSave })

  const savedValues = getPresetsSettingsDefaultValues(predefinedPresetsSettingsFormValues)
  const previewPatch =
    isDirty && hasLoadedSettings
      ? getPresetsPreviewPatch({
          savedValues,
          tradeNames: getPresetsSettingsTradeNames({ ...savedValues, ...debouncedContactValues }, queryClient),
          values: { ...savedValues, ...debouncedContactValues, currency, language },
        })
      : undefined

  useRegisterInvoicingSettingsPreviewPatch(previewPatch)

  // Replace the empty defaults with server values once the settings document has loaded.
  // Skip this while the user has unsaved edits, so a refetch cannot wipe them.
  // A failed fetch leaves the defaults in place and the fields unmounted, so Save cannot send them.
  useEffect(() => {
    if (!hasLoadedSettings || isDirty) {
      return
    }

    reset(getPresetsSettingsDefaultValues(predefinedPresetsSettingsFormValues))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isDirty is a guard, not a dependency: re-running when the form turns clean after save would reset over the values the host just kept
  }, [hasLoadedSettings, predefinedPresetsSettingsFormValues, reset])

  return (
    <Flex flexDirection="column" gap={Spacing.S24}>
      <Text variant="h2">{t('invoicing_settings.presets')}</Text>
      <Form>
        <PresetsSettingsFormFields hasFailedToLoad={hasFailedToLoad} isLoading={isLoading} onRetry={refetch} />
      </Form>
    </Flex>
  )
}
